export type ResultType = "Success" | "Failure" | "ExpectedFailure";

/**
 * A set of error codes to determine whether an encountered error should be treated as expected.
 */
export const expected_error_codes = new Set([
  "GeoLocInvalidRequest",
  "ItemDoesNotExist",
  "accessDenied",
  "activityLimitReached",
  "extensionError",
  "invalidRange",
  "invalidRequest",
  "itemNotFound",
  "malwareDetected",
  "nameAlreadyExists",
  "notAllowed",
  "notSupported",
  "quotaLimitReached",
  "resourceModified",
  "resyncRequired",
  "serviceNotAvailable",
  "syncStateNotFound",
  "unauthenticated"
]);

/**
 * Checks whether a service error is a network error from Fetch API. These
 * messages can happen when the network connection is slow, user closes the
 * tab or has a firewall setup which is blocking the connection. Such errors
 * should be classified as expected errors.
 * @param error The service error.
 * @returns True if the service error is a network error, false otherwise.
 */
function isNetworkFetchError(errorMessage: string): boolean {
  return (
    [
      "The user aborted a request.",
      "<safe>The user aborted a request.</safe>",
      "The operation was aborted. ",
      "<safe>The operation was aborted. </safe>",
      "Fetch is aborted",
      "<safe>Fetch is aborted</safe>",
      "Failed to fetch",
      "<safe>Failed to fetch</safe>",
      "NetworkError when attempting to fetch resource.",
      "<safe>NetworkError when attempting to fetch resource.</safe>",
      "The Internet connection appears to be offline.",
      "<safe>The Internet connection appears to be offline.</safe>",
      "Connection reset by peer",
      "<safe>Connection reset by peer</safe>",
      '{"status":-1,"code":"NoConnection"}',
      '<safe>{"status":-1,"code":"NoConnection"}</safe>',
      "Timeout",
      "<safe>Timeout</safe>",
      "Load failed"
    ].includes(errorMessage) || errorMessage.includes("Network issue")
  );
}

export function getFailureType(code: string, message?: string): ResultType {
  if (expected_error_codes.has(code) || isNetworkFetchError(message || "")) {
    return "ExpectedFailure";
  }
  return "Failure";
}
